import type {
  ParamOverride,
  ParameterSchema,
  ParameterSchemaBoolean,
  ParameterSchemaCategorical,
  ParameterSchemaNumeric,
} from "@tradingdroid/frontends-types";

// Actual modes - no more "default" as a pseudo-mode
export type OverrideMode = "fixed" | "range" | "categories";

export interface ParamInfo {
  name: string;
  schema: ParameterSchema | null;
  defaultValue: unknown;
  currentOverride: ParamOverride | undefined;
  isSchemaOnly: boolean;
}

// Type guards for override types
export interface FixedOverride {
  fixed: unknown;
}

export interface RangeOverride {
  min: number;
  max: number;
  step?: number;
}

export interface CategoriesOverride {
  categories: (string | boolean)[];
}

export const isFixedOverride = (override: ParamOverride | undefined): override is FixedOverride => {
  return override !== undefined && "fixed" in override;
};

export const isRangeOverride = (override: ParamOverride | undefined): override is RangeOverride => {
  return override !== undefined && "min" in override && "max" in override;
};

export const isCategoriesOverride = (
  override: ParamOverride | undefined,
): override is CategoriesOverride => {
  return override !== undefined && "categories" in override;
};

export const isNumericSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaNumeric => {
  return schema?.type === "IntParameter" || schema?.type === "FloatParameter";
};

export const isBooleanSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaBoolean => {
  return schema?.type === "BooleanParameter";
};

export const isCategoricalSchema = (
  schema: ParameterSchema | null,
): schema is ParameterSchemaCategorical => {
  return schema?.type === "CategoricalParameter";
};

// Get the current mode from override, or the default mode if no override
export const getOverrideMode = (
  override: ParamOverride | undefined,
  schema: ParameterSchema | null,
  defaultValue?: unknown,
): OverrideMode => {
  if (override) {
    if (isFixedOverride(override)) return "fixed";
    if (isRangeOverride(override)) return "range";
    if (isCategoriesOverride(override)) return "categories";
  }
  // No override = use default mode for this schema (considering default value)
  return getDefaultMode(schema, defaultValue);
};

// Get the default mode based on schema type and optimize flag.
// Priority: optimize flag → schema type → fixed fallback
export const getDefaultMode = (
  schema: ParameterSchema | null,
  _defaultValue?: unknown,
): OverrideMode => {
  if (!schema) return "fixed"; // No schema = fixed fallback

  const optimizeByDefault = schema.optimize !== false;

  if (!optimizeByDefault) {
    return "fixed";
  }

  // optimize=true: default to range for numeric, categories for bool/categorical
  if (isNumericSchema(schema)) {
    return "range";
  }
  if (isBooleanSchema(schema) || isCategoricalSchema(schema)) {
    return "categories";
  }

  return "fixed";
};

// Get available modes for a schema type
export const getAvailableModes = (schema: ParameterSchema | null): OverrideMode[] => {
  if (!schema) {
    return ["fixed", "range", "categories"];
  }

  if (isNumericSchema(schema)) {
    return ["range", "fixed"];
  }

  if (isBooleanSchema(schema) || isCategoricalSchema(schema)) {
    return ["categories", "fixed"];
  }

  return ["fixed"];
};

// Get display label for a mode, with (D) if it's the default
export const getModeLabel = (mode: OverrideMode, defaultMode: OverrideMode): string => {
  const isDefault = mode === defaultMode;
  const suffix = isDefault ? " (D)" : "";

  switch (mode) {
    case "fixed":
      return `Fixed${suffix}`;
    case "range":
      return `Range${suffix}`;
    case "categories":
      return `Values${suffix}`;
    default:
      return mode;
  }
};

// Check if the current mode matches the default (no override needed)
export const isUsingDefaultMode = (
  override: ParamOverride | undefined,
  schema: ParameterSchema | null,
  defaultValue?: unknown,
): boolean => {
  if (!override) return true;
  const currentMode = getOverrideMode(override, schema, defaultValue);
  const defaultMode = getDefaultMode(schema, defaultValue);
  return currentMode === defaultMode;
};

export const getTypeLabel = (schema: ParameterSchema | null): string => {
  if (!schema) return "unknown";
  switch (schema.type) {
    case "IntParameter":
      return "int";
    case "FloatParameter":
      return "float";
    case "CategoricalParameter":
      return "categorical";
    case "BooleanParameter":
      return "bool";
    default:
      return "unknown";
  }
};

/**
 * Get color for parameter type indicator.
 * Colors based on popular code editor themes (One Dark / Monokai):
 * - INT: Blue (numbers)
 * - FLOAT: Cyan (numbers, distinct from int)
 * - BOOL: Purple (special type)
 * - CATEGORICAL: Orange (enum/string-like)
 */
export const getTypeColor = (schema: ParameterSchema | null): string => {
  if (!schema) return "#6c757d"; // gray for unknown
  switch (schema.type) {
    case "IntParameter":
      return "#61afef"; // blue
    case "FloatParameter":
      return "#56b6c2"; // cyan
    case "BooleanParameter":
      return "#c678dd"; // purple
    case "CategoricalParameter":
      return "#d19a66"; // orange
    default:
      return "#6c757d"; // gray
  }
};

/** Short type indicator for compact display */
export const getTypeIndicator = (schema: ParameterSchema | null): string => {
  if (!schema) return "?";
  switch (schema.type) {
    case "IntParameter":
      return "I";
    case "FloatParameter":
      return "F";
    case "BooleanParameter":
      return "B";
    case "CategoricalParameter":
      return "C";
    default:
      return "?";
  }
};
